/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import setSliderDrawerIsOpen from "@store/Handlers/SetSliderDrawerIsOpen"; // Store Custom Code
import { dispatchAddToCartEvent } from "@store/Services/AnalyticsService"; // Store Custom Code
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import get from "@insite/mobius/utilities/get";
import { css } from "styled-components";
/* Store Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    analyticsId?: string; // Store Custom Code
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        addingProductToCart: state.context.addingProductToCart,
        currentCurency: state.context.session.currency?.currencyCode, // Store Custom Code
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading, // Store Custom Code
        cartLines: getCurrentCartState(state).value?.cartLines, // Store Custom Code
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
    setSliderDrawerIsOpen, // Store Custom Code
    loadCurrentCart, // Store Custom Code
};

/* Store Custom Code - Start */
export const productAddToCartButtonWithDispatchStyles: ButtonPresentationProps = {
    css: css`
        position: relative;
    `,
};

export const cartCount = (cartLines: CartLineModel[] | null | undefined, productId: string | null) => {
    const qtyBadge = {
        css: css`
            position: absolute;
            right: -10px;
            top: -10px;
            font-size: 12px;
            line-height: 25px;
            display: inline-block;
            text-align: center;
            width: 25px;
            height: 25px;
            background: #c9143b;
            color: white;
            border-radius: 50%;
            box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.3);
        `,
    };
    let qtyOrdered: number | null = 0;
    if (cartLines) {
        cartLines.forEach((cartLine: CartLineModel) => {
            if (cartLine.productId === productId) {
                qtyOrdered = cartLine.qtyOrdered;
            }
        });
    }
    return qtyOrdered ? <StyledWrapper {...qtyBadge}>{qtyOrdered}</StyledWrapper> : null;
};
/* Store Custom Code - End */

const ProductAddToCartButtonWithDispatch = ({
    productContext: {
        product: { id: productId },
        productInfo: { qtyOrdered, unitOfMeasure },
    },
    productContext, // Store Custom Code
    productSettings,
    hasEnoughInventory,
    addingProductToCart,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    onAddToCartClick,
    setSliderDrawerIsOpen, // Store Custom Code
    currentCurency, // Store Custom Code
    analyticsId, // Store Custom Code
    loadCurrentCart, // Store Custom Code
    shouldLoadCart, // Store Custom Code
    cartLines, // Store Custom Code
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonWithDispatchStyles, extendedStyles));

    /* Store Custom Code - Start */
    React.useEffect(() => {
        if (cartLines === undefined) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
        }
    }, [cartLines]);

    const sizeVariant = "medium";
    const label: TypographyPresentationProps = {
        weight: "bold",
    };
    /* Store Custom Code - End */

    if (!productSettings.canAddToCart || !hasEnoughInventory || !canAddToCart) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        setSliderDrawerIsOpen({ sliderDrawerIsOpen: true }); // Store Custom Code - We open a panel to display products on cart actions

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
        })) as CartLineModel;

        if (productSettings.showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
            // Store Custom Code
            dispatchAddToCartEvent(productContext, currentCurency); // Do not push a list on the PDP as it hijack existing tracking
        }
    };

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || addingProductToCart}
            {...otherProps}
        >
            {/* Store Custom Code - Start */}
            {cartCount(cartLines, productId)}
            <Typography
                {...label}
                size={get(buttonSizeVariants, [sizeVariant, "fontSize"]) || buttonSizeVariants.medium.fontSize}
            >
                {labelOverride ?? translate("Add to Cart")}
            </Typography>
            {/* Store Custom Code - End */}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButtonWithDispatch));
